import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import ClientLayout from "../components/ClientLayout";
import { ArrowLeft, Clock, Users, ImageOff } from "lucide-react";

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function ClientRecipeDetail() {
  const { token, recipeId, nameSlug = "client" } = useParams();
  const base = `/${nameSlug}/${token}`;
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get(`/client/${token}/recipes/${recipeId}`).then(({ data }) => setR(data));
  }, [token, recipeId]);
  if (!r) return <div className="min-h-screen grid place-items-center text-[hsl(var(--brand-muted))] font-serif-italic">Loading…</div>;

  const imgSrc = resolveImageUrl(r.image_url);

  return (
    <ClientLayout>
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-10" data-testid="client-recipe-detail-page">
        <Link to={`${base}/recipes`} className="text-sm text-[hsl(var(--brand-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--brand-ink))]">
          <ArrowLeft className="w-3 h-3" /> All recipes
        </Link>

        <div className="mt-6 grid md:grid-cols-[1fr_1.2fr] gap-10">
          <div>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))]">
              {imgSrc ? (
                <img src={imgSrc} alt={r.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[hsl(var(--brand-muted))]">
                  <div className="text-center">
                    <ImageOff className="w-10 h-10 mx-auto opacity-40" />
                    <div className="text-xs mt-2 font-serif-italic">No photo added</div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-[hsl(var(--brand-muted))]">
              {r.prep_time_minutes ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))]">
                  <Clock className="w-3.5 h-3.5" /> Prep {r.prep_time_minutes} min
                </span>
              ) : null}
              {r.yield_str ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))]">
                  <Users className="w-3.5 h-3.5" /> {r.yield_str}
                </span>
              ) : null}
              {(r.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[hsl(var(--brand-border))] capitalize">{t}</span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Recipe</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-2 leading-[0.95]">{r.name}</h1>
            {r.description ? <p className="font-serif-italic text-lg text-[hsl(var(--brand-muted))] mt-4">{r.description}</p> : null}

            <div className="mt-10">
              <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-sage))] mb-4">Ingredients</div>
              <ul className="space-y-2">
                {(r.ingredients || []).map((i, idx) => (
                  <li key={idx} className="flex gap-3 text-[15px]">
                    <span className="w-1 h-1 rounded-full bg-[hsl(var(--brand-sage))] mt-3 shrink-0" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-sage))] mb-4">Method</div>
              <ol className="space-y-5">
                {(r.instructions || []).map((step, idx) => (
                  <li key={idx} className="flex gap-4">
                    <div className="font-display text-3xl text-[hsl(var(--brand-sage))] leading-none shrink-0 w-9">{String(idx + 1).padStart(2, "0")}</div>
                    <div className="text-[15px] leading-relaxed pt-1">{step}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </ClientLayout>
  );
}
